import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { useHistory } from "react-router-dom";
import NewTicketModal from "../NewTicketModal";
import ContactModal from "../ContactModal";
import "./styles.css";

const dateText = value => {
  if (!value) return "Não informado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Não informado";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const moneyText = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Não informado";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(number);
};

const readable = value => {
  if (value === null || value === undefined || value === "") {
    return "Não informado";
  }
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Array.isArray(value)) {
    return value.map(readable).join(", ").slice(0, 400);
  }
  if (typeof value === "object") {
    return JSON.stringify(value).slice(0, 400);
  }
  return String(value).slice(0, 400);
};

const parseObject = value => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }
  if (typeof value !== "string") return {};
  try {
    const result = JSON.parse(value);
    return result && typeof result === "object" &&
      !Array.isArray(result) ? result : {};
  } catch {
    return {};
  }
};

const parseFields = fields => {
  if (Array.isArray(fields)) return fields;
  if (typeof fields === "string") {
    try {
      const parsed = JSON.parse(fields);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeResponse = (payload, fallback) => {
  const data = payload?.request || payload?.formRequest ||
    payload || {};
  const submission = data.submission ||
    payload?.submission || null;
  if (!submission) return null;

  const answers = parseObject(
    submission.answers || submission.answersJson ||
    data.answers
  );

  const fieldList = parseFields(
    data.fields || data.fieldsSnapshotJson ||
    fallback.fields || fallback.fieldsSnapshotJson
  );

  const labels = {};

  fieldList.forEach(field => {
    if (!field || typeof field !== "object") return;
    const label = field.label || field.title ||
      field.name || "Campo personalizado";

    [field.id, field.key, field.name].forEach(key => {
      if (key !== undefined && key !== null) {
        labels[String(key)] = String(label);
      }
    });
  });

  const values = Object.entries(answers).map(([key, value]) => ({
    key,
    label: labels[key] || key,
    value: readable(value)
  }));

  return {
    id: data.id || fallback.id,
    title: data.title || data.titleSnapshot ||
      fallback.title || fallback.titleSnapshot ||
      "Formulário respondido",
    submittedAt: data.submittedAt || submission.createdAt,
    importedAt: submission.importedAt,
    values
  };
};

const collectCustomFields = data => {
  const sources = [
    data?.extraInfo,
    data?.customFields,
    data?.contact?.extraInfo,
    data?.contact?.customFields
  ];

  const result = [];
  const seen = new Set();

  sources.forEach(source => {
    if (!Array.isArray(source)) return;
    source.forEach(item => {
      const name = item?.name || item?.label;
      const value = item?.value;

      if (!name || value === undefined || value === null ||
          value === "") return;

      const key = String(name).toLowerCase();

      if (seen.has(key)) return;
      seen.add(key);

      result.push({
        name: String(name),
        value: readable(value)
      });
    });
  });

  return result.slice(0, 50);
};

const Metric = ({ label, value, hint }) => (
  <div className="kl3-metric">
    <span className="kl3-metric-label">{label}</span>
    <strong className="kl3-metric-value">{value}</strong>
    {hint && <small className="kl3-metric-hint">{hint}</small>}
  </div>
);

export default function LeadIntelligencePanel({
  opportunity,
  contact,
  ticketId
}) {
  const hasOpportunity = Boolean(opportunity?.id);

  const contactId = contact?.id || opportunity?.contactId ||
    opportunity?.contact?.id;

  const leadHistory = useHistory();

  const [leadTicketOpen, setLeadTicketOpen] = useState(false);
  const [leadEmailEditOpen, setLeadEmailEditOpen] = useState(false);
  const [leadEmailOverride, setLeadEmailOverride] = useState(null);

  useEffect(() => {
    setLeadEmailOverride(null);
  }, [contactId]);

  const [data, setData] = useState({
    loading: false,
    forms: [],
    custom: [],
    formError: false
  });

  useEffect(() => {
    let active = true;

    setData({
      loading: true,
      forms: [],
      custom: collectCustomFields(contact),
      formError: false
    });

    const load = async () => {
      let custom = collectCustomFields(contact);
      let forms = [];
      let formError = false;

      if (contactId) {
        try {
          const response = await api.get(
            `/contacts/${encodeURIComponent(contactId)}`
          );
          custom = collectCustomFields(
            response.data?.contact || response.data
          ).concat(custom).filter((item, index, array) =>
            array.findIndex(other =>
              other.name.toLowerCase() === item.name.toLowerCase()
            ) === index
          );
        } catch {
          // Mantem os dados ja disponiveis no CRM.
        }
      }

      if (ticketId) {
        try {
          const response = await api.get(
            `/korax-forms/requests/ticket/${encodeURIComponent(ticketId)}`
          );

          const requests = Array.isArray(response.data?.requests)
            ? response.data.requests
            : [];

          const submitted = requests
            .filter(item => item.status === "submitted")
            .slice(0, 10);

          const results = await Promise.all(
            submitted.map(async request => {
              try {
                const detail = await api.get(
                  `/korax-forms/requests/${encodeURIComponent(request.id)}/submission`
                );
                return normalizeResponse(detail.data, request);
              } catch {
                formError = true;
                return null;
              }
            })
          );

          forms = results.filter(Boolean);
        } catch {
          formError = true;
        }
      }

      if (active) {
        setData({
          loading: false,
          forms,
          custom,
          formError
        });
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [opportunity?.id, contactId, ticketId]);

  const phone = String(
    contact?.number || opportunity?.contact?.number || ""
  ).replace(/\D/g, "");

  const email = String(
    leadEmailOverride === null
      ? (contact?.email || opportunity?.contact?.email || "")
      : leadEmailOverride
  ).trim();

  const leadInitialContact = useMemo(() => {
    const candidate = contact?.id
      ? contact
      : opportunity?.contact?.id
        ? opportunity.contact
        : null;

    if (candidate) {
      return {
        ...candidate,
        number: candidate.number || phone
      };
    }

    return contactId
      ? {
          id: Number(contactId),
          name: contact?.name ||
            opportunity?.contact?.name ||
            "Cliente",
          number: phone
        }
      : undefined;
  }, [contact, opportunity?.contact, contactId, phone]);

  // A rota /tickets/:ticketId recebe um UUID,
  // nunca o ID numerico do banco.
  const isTicketUuid = value =>
    typeof value === "string" &&
    /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i
      .test(value.trim());

  const handleLeadWhatsapp = () => {
    const linkedUuid = [
      ticketId,
      opportunity?.ticketUuid,
      opportunity?.ticket?.uuid,
      contact?.ticket?.uuid
    ].find(isTicketUuid);

    if (linkedUuid) {
      leadHistory.push(`/tickets/${linkedUuid.trim()}`);
      return;
    }

    // Quando so existe o ID numerico, reutiliza
    // o modal interno, que identifica atendimentos
    // existentes e permite selecionar a conexao.
    setLeadTicketOpen(true);
  };

  const handleLeadEmail = () => {
    if (!email) {
      setLeadEmailEditOpen(true);
      return;
    }

    leadHistory.push("/email", {
      koraxLeadComposeTo: email
    });
  };

  const handleLeadEmailEditClose = () => {
    setLeadEmailEditOpen(false);

    if (!contactId) return;

    api.get(`/contacts/${encodeURIComponent(contactId)}`)
      .then(({ data: response }) => {
        const updated = response?.contact || response;

        if (typeof updated?.email === "string") {
          setLeadEmailOverride(updated.email);
        }
      })
      .catch(() => {});
  };

  const followUpDate = opportunity?.nextFollowUpAt ||
    opportunity?.scheduledFollowUpAt || null;

  const totalResponses = useMemo(
    () => data.forms.reduce((total, form) =>
      total + form.values.length, 0),
    [data.forms]
  );

  return (
    <section className="kl3-root">
      <NewTicketModal
        modalOpen={leadTicketOpen}
        initialContact={leadInitialContact}
        onClose={ticket => {
          setLeadTicketOpen(false);

          if (isTicketUuid(ticket?.uuid)) {
            leadHistory.push(`/tickets/${ticket.uuid.trim()}`);
          } else if (ticket?.id) {
            console.warn(
              "Atendimento retornado sem UUID; navegacao cancelada."
            );
          }
        }}
      />

      <ContactModal
        open={leadEmailEditOpen}
        contactId={contactId}
        lockNumber
        onClose={handleLeadEmailEditClose}
        aria-labelledby="korax-lead-email-contact"
      />

      <header className="kl3-top">
        <div>
          <div className="kl3-eyebrow">
            KORAX CRM <span>VISÃO 360°</span>
          </div>
          <h2>Painel do lead</h2>
          <p>
            Informações, dados coletados e próximos passos
            em um único lugar.
          </p>
        </div>

        <div className="kl3-actions">
          {phone && contactId ? (
            <button
              type="button"
              className="kl3-button kl3-primary"
              onClick={handleLeadWhatsapp}
              title="Abrir atendimento dentro da Korax"
              style={{
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              WhatsApp
            </button>
          ) : (
            <span className="kl3-button kl3-disabled">
              WhatsApp indisponível
            </span>
          )}

          {email ? (
            <button
              type="button"
              className="kl3-button kl3-secondary"
              onClick={handleLeadEmail}
              title="Escrever e-mail dentro da Korax"
              style={{
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              Enviar e-mail
            </button>
          ) : contactId ? (
            <button
              type="button"
              className="kl3-button kl3-secondary"
              onClick={() => setLeadEmailEditOpen(true)}
              title="Cadastrar e-mail do cliente"
              style={{
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              Cadastrar e-mail
            </button>
          ) : (
            <span className="kl3-button kl3-disabled">
              E-mail não informado
            </span>
          )}
        </div>
      </header>

      <div className="kl3-metrics">
        <Metric
          label={hasOpportunity ? "Entrada no CRM" : "Contato cadastrado"}
          value={dateText(opportunity?.createdAt || contact?.createdAt)}
        />
        <Metric
          label="Valor da oportunidade"
          value={hasOpportunity
            ? moneyText(opportunity?.value)
            : "Sem oportunidade"}
        />
        <Metric
          label="Atendimento atualizado"
          value={dateText(opportunity?.ticket?.updatedAt)}
        />
        <Metric
          label="Responsável"
          value={opportunity?.user?.name || "Não atribuído"}
        />
      </div>

      <div className="kl3-columns">
        <div className="kl3-panel kl3-forms">
          <div className="kl3-panel-heading">
            <div>
              <span className="kl3-overline">DADOS DO CLIENTE</span>
              <h3>Informações coletadas</h3>
              <p>
                Campos personalizados e respostas dos formulários
              </p>
            </div>
            <span className="kl3-count">
              {data.loading ? "…" : totalResponses + data.custom.length}
            </span>
          </div>

          {data.loading && (
            <div className="kl3-note">Carregando informações...</div>
          )}

          {data.custom.length > 0 && (
            <div className="kl3-field-group">
              <div className="kl3-group-label">
                Dados cadastrados no contato
              </div>
              <div className="kl3-field-grid">
                {data.custom.map((field, index) => (
                  <div
                    className="kl3-field"
                    key={`${field.name}-${index}`}
                  >
                    <span>{field.name}</span>
                    <strong>{field.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.forms.map(form => (
            <div className="kl3-field-group" key={form.id}>
              <div className="kl3-form-heading">
                <div>
                  <div className="kl3-group-label">
                    {form.title}
                  </div>
                  <small>
                    Respondido em {dateText(form.submittedAt)}
                  </small>
                </div>

                <span className={
                  form.importedAt
                    ? "kl3-pill kl3-pill-green"
                    : "kl3-pill kl3-pill-amber"
                }>
                  {form.importedAt
                    ? "Importado"
                    : "Aguardando revisão"}
                </span>
              </div>

              <div className="kl3-field-grid">
                {form.values.map(field => (
                  <div className="kl3-field" key={field.key}>
                    <span>{field.label}</span>
                    <strong>{field.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!data.loading && !data.custom.length &&
            !data.forms.length && (
              <div className="kl3-empty">
                Nenhum campo personalizado disponível neste
                atendimento.
              </div>
            )}

          {data.formError && (
            <div className="kl3-note">
              Algumas respostas não puderam ser carregadas.
              Verifique as permissões do atendimento.
            </div>
          )}

          <div className="kl3-footnote">
            As respostas são exibidas para consulta.
            A importação para o cadastro ou CRM continua
            exigindo confirmação.
          </div>
        </div>

        <div className="kl3-panel kl3-followup">
          <div className="kl3-panel-heading">
            <div>
              <span className="kl3-overline">OPERAÇÃO COMERCIAL</span>
              <h3>Acompanhamento</h3>
              <p>Prioridades e próximos movimentos</p>
            </div>
          </div>

          <div className="kl3-followup-item">
            <span>Próximo follow-up individual</span>
            <strong>
              {followUpDate
                ? dateText(followUpDate)
                : "Não informado"}
            </strong>
            {!followUpDate && (
              <small>
                Ainda não há um agendamento individual
                disponível nos dados deste lead.
              </small>
            )}
          </div>

          <div className="kl3-followup-item">
            <span>Previsão de fechamento</span>
            <strong>
              {dateText(opportunity?.expectedCloseDate)}
            </strong>
          </div>

          <div className="kl3-followup-item">
            <span>Produto ou serviço</span>
            <strong>
              {opportunity?.product || "Não informado"}
            </strong>
          </div>

          <div className="kl3-followup-item">
            <span>Situação da oportunidade</span>
            <strong>
              {!hasOpportunity
                ? "Sem oportunidade vinculada"
                : opportunity?.status === "won"
                ? "Ganha"
                : opportunity?.status === "lost"
                ? "Perdida"
                : "Em andamento"}
            </strong>
          </div>

          <div className="kl3-footnote">
            O histórico comercial completo continua disponível
            na seção Histórico, logo abaixo deste painel.
          </div>
        </div>
      </div>
    </section>
  );
}
